import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { API_URLS } from "../App";
import api from "../hooks/api";
import { useLayoutContext } from "../context/LayoutContext";
import ListHighlightItems from "../components/Highlights/ListHighlightItems";
import ManageHighlight from "../components/Highlights/ManageHighlight";
import { Highlight } from "../model/Highlight";

const HighlightsManagementPage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { setSystemError, setLoading, setError, setSuccess, setModalScreen } =
    useLayoutContext();

  const [highlights, setHighlights] = useState<Highlight[]>([]);
  const [loading, setLocalLoading] = useState(false);

  const fetchHighlights = async () => {
    if (highlights.length === 0) {
      setLocalLoading(true);
    }
    setError(null);
    setSuccess(null);
    try {
      const response = await api.get(API_URLS.HIGHLIGHTS);
      setHighlights(response.data || []);
    } catch (error: any) {
      console.log("Error fetching highlights:", error.message);
      setError("Erro ao carregar destaques");
    } finally {
      setLocalLoading(false);
    }
  };

  const handleHighlightUpdated = useCallback(
    async (highlight: Highlight) => {
      const existingHighlight = highlights.find((h) => h.id === highlight.id);
      if (!existingHighlight) {
        setHighlights((prev) => [highlight, ...prev]);
        setSuccess(`Destaque "${highlight.title}" criado com sucesso!`);
      } else {
        setHighlights((prev) =>
          prev.map((h) => (h.id === highlight.id ? highlight : h)),
        );
        setSuccess(`Destaque "${highlight.title}" atualizado com sucesso!`);
      }
    },
    [highlights, setSuccess],
  );

  const handleHighlightDeleted = (highlight: Highlight) => {
    setHighlights((prev) => prev.filter((h) => h.id !== highlight.id));
    setSuccess(`Destaque "${highlight.title}" apagado com sucesso!`);
  };

  const openManageHighlight = (highlight?: Highlight) => {
    setModalScreen({
      state: "edit",
      title: highlight?.id
        ? `Editar destaque: ${highlight.title}`
        : "Criar novo destaque",
      children: (
        <ManageHighlight
          highlight={highlight}
          onHighlightUpdate={handleHighlightUpdated}
          setError={setError}
          setSuccess={setSuccess}
        />
      ),
      onClose: () => {
        setModalScreen(null);
      },
    });
  };

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate("/login", { state: { from: pathname } });
    }
    fetchHighlights();
  }, []);

  if (!isAuthenticated()) {
    return <div>You must be logged in to manage highlights.</div>;
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="flex flex-col gap-6">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <span className="text-3xl font-bold text-gray-900 tracking-tight">
              Destaques
            </span>
            <p className="text-gray-500 mt-1">
              Gerencie os destaques exibidos na página inicial.
            </p>
          </div>
          <button
            onClick={() => openManageHighlight()}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition"
          >
            + Criar Destaque
          </button>
        </div>

        {/* Content */}
        <div className="overflow-auto">
          {loading ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <p className="mt-4 text-gray-600">A carregar destaques...</p>
              </div>
            </div>
          ) : (
            <ListHighlightItems
              data={highlights}
              handleManageItem={openManageHighlight}
              handleDeleteItem={handleHighlightDeleted}
              setError={setError}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default HighlightsManagementPage;
